import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Database, Download, Trash2, RefreshCw, Clock, 
  HardDrive, CheckCircle, AlertCircle, Plus, FileArchive,
  FileText, Image, RotateCcw, Upload, Settings2, DownloadCloud
} from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

import { useTranslation } from 'react-i18next';
import BackupCustomizeModal from '../components/BackupCustomizeModal';
import MigrateFromPreviewModal from '../components/MigrateFromPreviewModal';
import FactoryResetModal from '../components/FactoryResetModal';

const Backup = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [factoryResetOpen, setFactoryResetOpen] = useState(false);
  const fileInputRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/backup/status');
      setStatus(response.data);
    } catch (error) {
      toast.error('فشل في جلب حالة النسخ الاحتياطي');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleCreateBackup = async () => {
    try {
      setCreating(true);
      toast.info(t('backup.starting_backup_in_background'));
      const response = await axiosInstance.post('/backup/create');
      // The endpoint now returns immediately and the job runs in background.
      // Poll status every 3s until completion.
      const pollStart = Date.now();
      const maxWaitMs = 10 * 60 * 1000;  // 10 minutes
      let done = false;
      while (!done && (Date.now() - pollStart) < maxWaitMs) {
        await new Promise(r => setTimeout(r, 3000));
        try {
          const s = await axiosInstance.get('/backup/job-status');
          const job = s.data;
          if (job.status === 'completed') {
            done = true;
            toast.success(`تم إنشاء النسخة الاحتياطية: ${job.filename} (${job.size_mb} MB)`);
            if (job.filename) {
              await handleDownload(job.filename);
            }
            break;
          }
          if (job.status === 'failed') {
            done = true;
            toast.error(`فشل: ${job.error || 'unknown'}`);
            break;
          }
          // still running — keep polling silently
        } catch (e) {
          // ignore transient poll errors
        }
      }
      if (!done) {
        toast.warning('الوقت طويل جداً. يرجى تحديث الصفحة لاحقاً للتحقق.');
      }
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل في إنشاء النسخة الاحتياطية');
    } finally {
      setCreating(false);
    }
  };

  const handleDownload = async (filename) => {
    try {
      toast.info('جاري تحميل الملف... سيتم حفظه في مجلد Downloads');
      const response = await axiosInstance.get(`/backup/download/${filename}`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('تم التحميل بنجاح! يمكنك نقل الملف إلى E:\\DHR Company\\Back Up');
    } catch (error) {
      toast.error('فشل في تحميل الملف');
    }
  };

  const handleDownloadDbJson = async () => {
    try {
      toast.info(t('backup.downloading_complete_backup'));
      const response = await axiosInstance.get('/backup/download-full-stream', {
        responseType: 'blob',
        timeout: 300000  // 5 minutes for large backups
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      link.href = url;
      link.setAttribute('download', `full_backup_${ts}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t('backup.full_backup_downloaded'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('backup.download_failed'));
    }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`هل أنت متأكد من حذف ${filename}؟`)) return;
    
    try {
      await axiosInstance.delete(`/backup/${filename}`);
      toast.success('تم حذف النسخة الاحتياطية');
      fetchStatus();
    } catch (error) {
      toast.error('فشل في حذف النسخة الاحتياطية');
    }
  };

  const handleRestore = async (filename) => {
    const confirmMsg = `⚠️ تحذير: استرجاع النسخة الاحتياطية سيستبدل جميع البيانات الحالية!

هل أنت متأكد من استرجاع: ${filename}؟

ملاحظة: يُنصح بإنشاء نسخة احتياطية من البيانات الحالية قبل الاسترجاع.`;
    
    if (!window.confirm(confirmMsg)) return;
    
    try {
      setRestoring(filename);
      toast.info('جاري استرجاع النسخة الاحتياطية... الرجاء الانتظار');
      
      const response = await axiosInstance.post(`/backup/restore/${filename}`);
      
      toast.success(`${response.data.message} - تم استرجاع ${response.data.total_documents} سجل`);
      
      // Show details
      if (response.data.restored_collections) {
        console.log('Collections restored:', response.data.restored_collections);
      }
      
      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل في استرجاع النسخة الاحتياطية');
    } finally {
      setRestoring(null);
    }
  };

  const handleUploadRestore = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.zip') && !file.name.endsWith('.json')) {
      toast.error('يجب أن يكون الملف بصيغة ZIP أو JSON');
      return;
    }

    const confirmMsg = `⚠️ تحذير: استرجاع النسخة الاحتياطية سيستبدل جميع البيانات الحالية!

هل أنت متأكد من استرجاع الملف: ${file.name}؟

ملاحظة: يُنصح بإنشاء نسخة احتياطية من البيانات الحالية قبل الاسترجاع.`;

    if (!window.confirm(confirmMsg)) {
      event.target.value = '';
      return;
    }

    try {
      setUploading(true);
      toast.info('جاري رفع واستعادة النسخة الاحتياطية... الرجاء الانتظار');

      const formData = new FormData();
      formData.append('file', file);

      const response = await axiosInstance.post('/backup/upload-restore', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success(`${response.data.message} - تم استرجاع ${response.data.total_documents} سجل`);

      if (response.data.restored_collections) {
        console.log('Collections restored:', response.data.restored_collections);
      }

      fetchStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل في رفع واستعادة النسخة الاحتياطية');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const formatSize = (backup) => {
    if (backup.size_mb && backup.size_mb >= 1) {
      return `${backup.size_mb} MB`;
    }
    return `${backup.size_kb} KB`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="backup-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">{t('backup.backup')}</h1>
          <p className="text-gray-500 mt-1">
            إدارة النسخ الاحتياطية الكاملة (قاعدة البيانات + الملفات)
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setMigrateOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg font-medium shadow-md transition-all"
            data-testid="migrate-from-preview-btn"
            title="نقل بيانات كاملة من بيئة معاينة (أو أي بيئة أخرى) بنقرة واحدة — مخصص لأول نشر إنتاجي"
          >
            <DownloadCloud className="w-4 h-4" />
            🔄 نقل من Preview
          </button>
          <button
            onClick={() => setFactoryResetOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium shadow-md transition-all"
            data-testid="factory-reset-btn"
            title="مسح كل بيانات هذه البيئة والبدء من جديد — يتطلب تأكيد بكتابة عبارة"
          >
            <Trash2 className="w-4 h-4" />
            🗑️ مسح كامل
          </button>
          <button
            onClick={fetchStatus}
            className="btn-secondary flex items-center gap-2"
            data-testid="refresh-backup-btn"
          >
            <RefreshCw className="w-4 h-4" /> تحديث
          </button>
          
          {/* Upload & Restore Button */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUploadRestore}
            accept=".zip,.json"
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-secondary flex items-center gap-2 bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
            data-testid="upload-restore-btn"
          >
            {uploading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {uploading ? 'جاري الرفع...' : 'رفع واستعادة من ملف'}
          </button>
          
          <button
            onClick={handleDownloadDbJson}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-300 rounded-lg font-medium transition-colors"
            data-testid="download-db-json-btn"
            title="تنزيل نسخة كاملة (DB + الصور + PDF) مباشرة بدون استهلاك مساحة Production"
          >
            <Download className="w-4 h-4" />
            تنزيل نسخة كاملة مباشرة
          </button>

          <button
            onClick={() => setCustomizeOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-300 rounded-lg font-medium transition-colors"
            data-testid="customize-backup-btn"
            title="اختر الصفحات التي تريد حفظ بياناتها فقط"
          >
            <Settings2 className="w-4 h-4" />
            تخصيص
          </button>
          
          <button
            onClick={handleCreateBackup}
            disabled={creating}
            className="btn-primary flex items-center gap-2"
            data-testid="create-backup-btn"
          >
            {creating ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {creating ? 'جاري الإنشاء...' : 'إنشاء نسخة احتياطية كاملة'}
          </button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
          <div>
            <h3 className="font-semibold text-amber-800">تحميل تلقائي للنسخ الاحتياطية</h3>
            <p className="text-amber-700 text-sm mt-1">
              عند إنشاء نسخة احتياطية جديدة، سيتم تحميلها <strong>تلقائياً</strong> إلى جهازك.
            </p>
            <div className="mt-2 p-2 bg-amber-100 rounded-lg">
              <p className="text-amber-800 text-sm font-medium">💡 لتحميل الملفات مباشرة إلى مجلد محدد:</p>
              <ol className="text-amber-700 text-sm mt-1 list-decimal list-inside space-y-1">
                <li>افتح إعدادات المتصفح (Chrome/Edge)</li>
                <li>ابحث عن "Downloads" أو "التنزيلات"</li>
                <li>فعّل خيار <strong>"Ask where to save each file"</strong></li>
                <li>عند التحميل، اختر المسار: <code className="bg-amber-200 px-1 rounded">E:\DHR Company\Back Up</code></li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Database className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">إجمالي النسخ</p>
              <p className="text-2xl font-bold text-gray-800">
                {status?.total_backups || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">الحد الأقصى</p>
              <p className="text-2xl font-bold text-gray-800">
                {status?.max_backups || 7} نسخ
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-orange-100 rounded-lg">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">الملفات المرفقة</p>
              <p className="text-2xl font-bold text-gray-800">
                {status?.uploads_info?.total_files || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <HardDrive className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">حجم الملفات</p>
              <p className="text-2xl font-bold text-gray-800">
                {status?.uploads_info?.total_size_mb || 0} MB
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Info Alert */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <FileArchive className="w-5 h-5 text-green-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-green-800">نسخة احتياطية كاملة (ZIP)</h4>
            <ul className="mt-1 text-sm text-green-700 list-disc list-inside">
              <li>تشمل جميع بيانات قاعدة البيانات (العملاء، المشاريع، الفواتير، إلخ)</li>
              <li>تشمل جميع الملفات المرفقة (PDF، صور)</li>
              <li>يمكن استخدامها لاستعادة النظام بالكامل</li>
              <li>يتم الاحتفاظ بآخر {status?.max_backups || 7} نسخ فقط</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Backups List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <div className="p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">
            النسخ الاحتياطية المتاحة
          </h2>
        </div>
        
        {status?.backups?.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {status.backups.map((backup, index) => (
              <div 
                key={backup.filename}
                className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                data-testid={`backup-item-${index}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-2 rounded-lg ${backup.type === 'full' ? 'bg-green-100' : 'bg-blue-100'}`}>
                    {backup.type === 'full' ? (
                      <FileArchive className={`w-5 h-5 ${backup.type === 'full' ? 'text-green-600' : 'text-blue-600'}`} />
                    ) : (
                      <Database className="w-5 h-5 text-blue-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{backup.filename}</p>
                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {formatDate(backup.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <HardDrive className="w-4 h-4" />
                        {formatSize(backup)}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        backup.type === 'full' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {backup.type === 'full' ? 'كاملة (DB + Files)' : 'قاعدة بيانات فقط'}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleRestore(backup.filename)}
                    disabled={restoring === backup.filename}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                    title="استرجاع النسخة الاحتياطية"
                    data-testid={`restore-backup-${index}`}
                  >
                    {restoring === backup.filename ? (
                      <RefreshCw className="w-5 h-5 animate-spin" />
                    ) : (
                      <RotateCcw className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => handleDownload(backup.filename)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="تحميل"
                    data-testid={`download-backup-${index}`}
                  >
                    <Download className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(backup.filename)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="حذف"
                    data-testid={`delete-backup-${index}`}
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            <Database className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>لا توجد نسخ احتياطية</p>
            <p className="text-sm mt-1">اضغط على "إنشاء نسخة احتياطية كاملة" لإنشاء نسخة جديدة</p>
          </div>
        )}
      </div>

      <BackupCustomizeModal
        open={customizeOpen}
        onClose={() => setCustomizeOpen(false)}
        onBackupComplete={fetchStatus}
      />
      <MigrateFromPreviewModal
        open={migrateOpen}
        onClose={() => setMigrateOpen(false)}
        onSuccess={fetchStatus}
      />
      <FactoryResetModal
        open={factoryResetOpen}
        onClose={() => setFactoryResetOpen(false)}
        onSuccess={() => {
          // After factory reset, the current JWT is stale — force
          // logout by clearing localStorage and redirecting to /login.
          try {
            localStorage.clear();
            sessionStorage.clear();
          } catch (_) { /* ignore */ }
          window.location.href = '/login';
        }}
      />
    </div>
  );
};

export default Backup;
